type SitecoreContextMappingType = {
  sitecoreContextId: string;
  type: string;
  edgeEndpoint?: string;
  personalizeTenant: string;
  searchTenant: string;
};

type SitecoreContextType = {
  sitecoreContextId: string;
  name: string;
  description: string;
  organizationId: string;
  type: string;
  accountId: string;
  resources: SitecoreContextResourceType[];
  edgeEndpoint?: string;
  personalizeTenant?: string;
  searchTenant?: string;
  createdAt?: string;
  updatedAt?: string;
};

type ExtendedSitecoreContextType = SitecoreContextType & {
  filteredContexts?: string[]; // Array of scoped context IDs
  isScopedContext?: boolean;
  masterSitecoreContextId?: string; // Parent context ID for scoped contexts
  isError?: boolean; // Indicates an error state (e.g., failed to load scoped contexts)
  isLoading?: boolean; // Indicates a loading state (e.g., skeleton rows)
};

// V2 list endpoint response — cursor pagination via the optional `next` continuation token
type ListSitecoreContextsResponse = {
  next?: string;
  data: SitecoreContextType[];
};

type SitecoreContextResourceType = {
  id: string;
  name: string;
  type: string;
  tenantId: string;
  tenantName: string;
  tenantDisplayName: string;
  productCodes?: string[];
  tenantProductCode?: string;
  organizationId?: string; // Optional organization ID that might be present in the resource
  properties: SitecoreContextResourcesPropertyType[];
};

// Enhanced resource type that includes properties added by useEnhancedResources
type EnhancedSitecoreContextResourceType = SitecoreContextResourceType & {
  isVisible: boolean;
  isScopedResource: boolean;
};

type SitecoreContextResourcesPropertyType = {
  id: string;
  value: string;
};

type CreateSitecoreContextType = {
  resources: SitecoreContextResourceType[];
};

type AddEditContextMappingSelectOption = {
  id: string;
  displayName: string;
  projectName?: string;
  environmentName?: string;
};

export type TGenerateContextIdPayload = {
  name: string;
  description: string;
};

export type TCreateScopedContextPayload = {
  sitecoreContextId: string;
  allowedResourceIds: string[];
  name: string;
};

export type TCreateSearchResourcePayload = {
  id: string;
  name: string;
  type: string;
  tenantId: string;
  properties: SitecoreContextResourcesPropertyType[];
};

export type TUpsertContextProductPayload = {
  productCode: 'XMCloud';
  tenantId: string;
  environment: 'Live' | 'Preview';
};

type FilteredContext = {
  sitecoreContextId: string;
  masterSitecoreContextId: string;
  allowedResourceIds: string[];
};

type FilteredContextWithResources = {
  sitecoreContextId: string;
  name: string;
  masterSitecoreContextId: string;
  resources: SitecoreContextResourceType[];
  createdAt?: string;
  updatedAt?: string;
};

export type TScopedContext = {
  masterSitecoreContextId: string;
  updatedAt: Date;
  createdAt: Date;
} & TCreateScopedContextPayload;

type ResourceType = {
  id: string;
  type: string;
  tenantId: string;
  tenantName: string;
};

type ResourceByProductType = {
  tenantId: string;
  tenantName: string;
  productCode: string;
  environment?: string;
  showHeader?: boolean;
};

type AvailableProduct = {
  productCode: string;
  tenantId: string;
  tenantName: string;
  environment?: 'Live' | 'Preview';
};

type TenantApplicationNode = {
  id: string;
  annotations: { key: string; value: string }[];
};

type DeploymentInfo = {
  projectId: string;
  environmentId: string;
  isLoading: boolean;
  error: Error | null;
};

enum RegenerationErrorType {
  MISSING_PROJECT_ID = 'MISSING_PROJECT_ID',
  MISSING_ENVIRONMENT_ID = 'MISSING_ENVIRONMENT_ID',
  MISSING_ORGANIZATION_ID = 'MISSING_ORGANIZATION_ID',
  TENANT_NOT_FOUND = 'TENANT_NOT_FOUND',
  NO_ANNOTATIONS = 'NO_ANNOTATIONS',
  GENERAL_ERROR = 'GENERAL_ERROR',
  NO_TENANT_APPLICATIONS = 'NO_TENANT_APPLICATIONS',
  DATA_RETRIEVAL_ERROR = 'DATA_RETRIEVAL_ERROR',
}

type ErrorMessageMap = {
  [key in RegenerationErrorType]: string;
};

export {
  SitecoreContextMappingType,
  SitecoreContextType,
  ExtendedSitecoreContextType,
  ListSitecoreContextsResponse,
  SitecoreContextResourceType,
  EnhancedSitecoreContextResourceType,
  SitecoreContextResourcesPropertyType,
  FilteredContext,
  FilteredContextWithResources,
  CreateSitecoreContextType,
  AddEditContextMappingSelectOption,
  ResourceType,
  ResourceByProductType,
  AvailableProduct,
  TenantApplicationNode,
  DeploymentInfo,
  RegenerationErrorType,
  ErrorMessageMap,
};
